import { Component } from '@verndale/core';
import { breakpoints } from '../../helpers/variables';
import { ElementOrSelector, stagger, timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { EventBusSingleton } from '../../animation/eventBus';

class HeaderSearch extends Component {
  MOTION_ZERO_DURATION = 0.000001;
  breakpoint = window.matchMedia(
    `(min-width: ${breakpoints.tabletLandscape}px)`
  );
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  constructor(el: HTMLElement) {
    super(el);
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      closeButton: this.el.querySelector<HTMLElement>(
        '.header-search__close-btn'
      ),
      inputField: this.el.querySelector<HTMLElement>('.header-search__input'),
      topSection: this.el.querySelector<HTMLElement>(
        '.header-search__top-section'
      ),
      featuredTopicsSection: this.el.querySelector<HTMLElement>(
        '.header-search__featured-section'
      ),
      featuredTopicsTitle: this.el.querySelector<HTMLElement>(
        '.header-search__featured-topics-title'
      ),
      featuredTopicsItems: this.el.querySelectorAll<HTMLElement>(
        '.header-search__featured-topics-items > *'
      ),
      featuredMenuItem: this.el.querySelector<HTMLElement>(
        '.featured-menu-item'
      ),
      backButtonMobile: this.el.querySelector<HTMLElement>(
        '.header-search__back-button-mobile'
      ),
      form: this.el.querySelector<HTMLElement>('.header-search__form')
    };
  }

  addListeners() {
    (this.dom.closeButton as HTMLElement).addEventListener(
      'click',
      this.handleClose.bind(this)
    );
    (this.dom.inputField as HTMLElement).addEventListener(
      'click',
      this.handleMobileTap.bind(this)
    );
    (this.dom.inputField as HTMLElement).addEventListener(
      'input',
      this.handleInput.bind(this)
    );

    (this.dom.backButtonMobile as HTMLElement).addEventListener(
      'click',
      this.handleClose.bind(this)
    );
    (this.dom.form as HTMLElement).addEventListener(
      'submit',
      this.handleFormSubmit.bind(this)
    );
    document.addEventListener(
      'click',
      this.handleClickOutside.bind(this),
      true
    );
  }

  handleClickOutside(e: Event) {
    if (!this.breakpoint.matches) return;
    if (
      !(this.dom.el as HTMLElement).contains(e.target as Node) &&
      !(e.target as HTMLElement)?.classList?.contains(
        'utility-navigation__search-button'
      )
    ) {
      this.closeSearch();
    }
  }

  handleInput() {
    const searchInput = this.dom.inputField as HTMLInputElement;
    if (searchInput.value.length > 0) {
      (this.dom.form as HTMLElement).classList.remove(
        'header-search__input--error'
      );
    }
  }

  handleMobileTap() {
    if (this.dom.featuredTopicsSection && !this.breakpoint.matches) {
      EventBusSingleton.publish('utilityNavigation:openingHeaderSearch');
      (this.dom.el as HTMLElement).classList.add('featured-topics--open');
    }
  }

  handleFormSubmit(e: Event) {
    const searchInput = this.dom.inputField as HTMLInputElement;
    if (!searchInput.value) {
      e.preventDefault();
      (this.dom.form as HTMLElement).classList.add(
        'header-search__input--error'
      );
    }
  }

  handleClose() {
    (this.dom.el as HTMLElement).classList.remove('featured-topics--open');
    (this.dom.form as HTMLElement).classList.remove(
      'header-search__input--error'
    );
    (this.dom.inputField as HTMLInputElement).value = '';
    this.closeSearch();
    EventBusSingleton.publish('utilityNavigation:closingHeaderSearch');
  }

  openSearch() {
    if (this.el.classList.contains('header-search--open')) return;
    this.el.style.display = 'block';
    const staggerItems = [] as HTMLElement[];
    staggerItems.push(this.dom.featuredTopicsTitle as HTMLElement);
    staggerItems.push(
      ...(this.dom.featuredTopicsItems as NodeListOf<HTMLElement>)
    );

    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push({ name: 'featured-item', at: 0.25 });

    sequence.push([
      this.dom.el as ElementOrSelector,
      { clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0 0)'] },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: 'start'
      }
    ]);
    sequence.push([
      this.dom.topSection as ElementOrSelector,
      { clipPath: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'] },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.45,
        at: this.reducedMotion.matches ? 'start' : 'featured-item'
      }
    ]);
    sequence.push([
      staggerItems as ElementOrSelector,
      {
        opacity: [0, 1],
        transform: ['translate(-20px, 0)', 'translate(0, 0)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.35,
        delay: this.reducedMotion.matches
          ? this.MOTION_ZERO_DURATION
          : stagger(0.05),
        at: this.reducedMotion.matches ? 'start' : 'featured-item'
      }
    ]);
    sequence.push([
      this.dom.featuredMenuItem as ElementOrSelector,
      {
        opacity: [0, 1],
        transform: ['translate(0, 50px)', 'translate(0, 0)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: this.reducedMotion.matches ? 'start' : 'featured-item'
      }
    ]);

    timeline(sequence).finished.then(() => {
      [].forEach.call(staggerItems, (item: HTMLElement) => {
        item.removeAttribute('style');
      });
      this.el.classList.add('header-search--open');
    });
  }

  async closeSearch() {
    if (!this.el.classList.contains('header-search--open')) return;

    const staggerItems = [] as HTMLElement[];
    staggerItems.push(this.dom.featuredTopicsTitle as HTMLElement);
    staggerItems.push(
      ...(this.dom.featuredTopicsItems as NodeListOf<HTMLElement>)
    );
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      staggerItems.reverse() as ElementOrSelector,
      {
        opacity: [1, 0],
        transform: ['translate(0, 0)', 'translate(-25px, 0)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25,
        delay: this.reducedMotion.matches
          ? this.MOTION_ZERO_DURATION
          : stagger(0.05),
        at: 'start'
      }
    ]);
    sequence.push([
      this.dom.featuredMenuItem as ElementOrSelector,
      {
        opacity: [1, 0],
        transform: ['translate(0, 0)', 'translate(-25px, 0)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25,
        delay: 0,
        at: 'start'
      }
    ]);
    sequence.push([
      this.dom.el as ElementOrSelector,
      { clipPath: ['inset(0 0 0 0)', 'inset(0 0 100% 0)'] },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25,
        at: this.reducedMotion.matches ? 'start' : '-0.25'
      }
    ]);
    sequence.push([
      this.dom.topSection as ElementOrSelector,
      { clipPath: ['inset(0 0 0 0)', 'inset(0 100% 0 0)'] },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25,
        at: this.reducedMotion.matches ? 'start' : '-0.25'
      }
    ]);
    await timeline(sequence).finished.then(() => {
      this.el.classList.remove('header-search--open');
      this.el.classList.remove('featured-topics--open');

      this.el.removeAttribute('style');
      staggerItems.forEach((item: HTMLElement) => {
        item.removeAttribute('style');
      });
    });
  }
}

export default HeaderSearch;
